import {css, useTheme} from '@mui/material';
import {
  ConfigObjectType,
  GetStylesFunction,
  LoadingSpinner,
  NoDataMessageWithIcon,
  serializeJSONToString,
  useApiQuery,
} from '@wallaby-ui/core';
import classNames from 'classnames';
import {isEmpty} from 'lodash';
import * as React from 'react';
import {useEffect, useMemo, useState} from 'react';

import {DiagnosisLaboratoryResultsItem} from './DiagnosisLaboratoryResultsItem';
import {formatLabMethodName, indexLabMethodTypes, LabResultEventTypeV3Dto} from 'src/js/common/labMethodLabel';
import {MerieSchema, WallabyCoreSchema} from 'src/js/common/types';

export interface DiagnosisLaboratoryResultsOwnProps {
  config?: ConfigObjectType;
  encounterId?: string | number;
  filters: Record<string, any>;
  pharmaRecommendations: MerieSchema.components['schemas']['PharmaRecommendationsDto'] | undefined;
  chartEvents: MerieSchema.components['schemas']['EncounterTimelineChartEventsDto'] | undefined;
}

type Props = DiagnosisLaboratoryResultsOwnProps;

export const DiagnosisLaboratoryResults: React.FC<Props> = (props) => {
  const {pharmaRecommendations, encounterId, config, filters, chartEvents} = props;
  const chartApiUrl = config?.service.url.chart;
  const apiParams = {} as any;
  if (filters.dateFrom) apiParams.startDate = filters.dateFrom;
  if (filters.dateTo) apiParams.endDate = filters.dateTo;
  if (filters.laboratories) apiParams.labResultEventGroups = filters.laboratories;
  if (filters.labResultEventTypes) apiParams.labResultEventTypes = filters.labResultEventTypes;
  const chartUrlSuffix = 'chart-v3';

  const {
    data,
    isLoading: timelineChartLoading,
    isError,
  } = useApiQuery<WallabyCoreSchema.components['schemas']['EncounterTimelineChartV3Dto']>(
    [chartApiUrl, encounterId, serializeJSONToString(apiParams), chartUrlSuffix],
    null,
    {
      axiosConfig: {
        url: `${chartApiUrl}/${encounterId}/${chartUrlSuffix}`,
        params: new URLSearchParams(apiParams),
      },
    }
  );

  const supportedTypesUrl = `${config?.service.url.chartRoot}/supported-event-types-v3`;
  const {data: supportedTypes, isLoading: supportedTypesLoading} = useApiQuery<LabResultEventTypeV3Dto[]>(
    [supportedTypesUrl],
    null,
    {
      axiosConfig: {url: supportedTypesUrl},
    }
  );

  const labMethodTypeMap = useMemo(() => indexLabMethodTypes(supportedTypes), [supportedTypes]);

  const isDataLoading = timelineChartLoading || supportedTypesLoading;

  // Stay on the spinner one paint past data arrival so users don't see a flash of "No data" tiles
  // while items with data are still mounting their plotly charts. This is the cheaper of the two
  // remedies in the spec ("spinner until graphs rendered" vs. infinite scroll).
  const [chartsReady, setChartsReady] = useState(false);
  useEffect(() => {
    if (isDataLoading) {
      setChartsReady(false);
      return undefined;
    }
    let cancelled = false;
    const raf1 = requestAnimationFrame(() => {
      const raf2 = requestAnimationFrame(() => {
        if (!cancelled) setChartsReady(true);
      });
      if (cancelled) cancelAnimationFrame(raf2);
    });
    return () => {
      cancelled = true;
      cancelAnimationFrame(raf1);
    };
  }, [isDataLoading, data]);

  const isLoading = isDataLoading || !chartsReady;

  const theme = useTheme();
  const styles = getStyles({theme, params: {isLoading}});

  // Aggregate "no laboratory results" only when there are no series at all. When series exist but
  // all are empty (e.g. the "Other" category), fall through to render a per-item "No data for [...]"
  // box for each value in the category.
  const noChartData = useMemo<boolean>(() => isEmpty(data) || isEmpty(data?.chartData), [data]);

  // Graphs with data first, then graphs without data. Within each group, hospital-named results
  // (names that do NOT start with '[') come before our technical names (which start with '[<code>]'),
  // each subgroup sorted A→Z by the constructed lab-method name.
  const sortedChartData = useMemo(() => {
    const chart = data?.chartData ?? [];
    const nameFor = (item: WallabyCoreSchema.components['schemas']['LabResultEventChartDataV3Dto']) => {
      const supported = labMethodTypeMap[item.eventType];
      return formatLabMethodName({
        eventType: item.eventType,
        label: supported?.label,
        unit: supported?.unit ?? item.unit,
      });
    };
    const withData: WallabyCoreSchema.components['schemas']['LabResultEventChartDataV3Dto'][] = [];
    const withoutData: WallabyCoreSchema.components['schemas']['LabResultEventChartDataV3Dto'][] = [];
    chart.forEach((item) => {
      (isEmpty(item.dataPoints) ? withoutData : withData).push(item);
    });
    const cmp = (a: typeof chart[number], b: typeof chart[number]) => {
      const nameA = nameFor(a);
      const nameB = nameFor(b);
      const technicalA = nameA.startsWith('[') ? 1 : 0;
      const technicalB = nameB.startsWith('[') ? 1 : 0;
      if (technicalA !== technicalB) return technicalA - technicalB;
      return nameA.localeCompare(nameB);
    };
    withData.sort(cmp);
    withoutData.sort(cmp);
    return [...withData, ...withoutData];
  }, [data, labMethodTypeMap]);

  if (isError) {
    return (
      <div css={styles.wrapper}>
        <NoDataMessageWithIcon message={'chart.noLaboratoryResult'} />
      </div>
    );
  }

  if (!isDataLoading && noChartData) {
    return (
      <div css={styles.wrapper}>
        <NoDataMessageWithIcon message={'chart.noLaboratoryResult'} />
      </div>
    );
  }

  // While data is loading we render the spinner in its original 220px area at the top
  // of the wrapper. Once data lands we still keep the spinner visible for ~1 paint, but
  // mount the chart items off-flow (position:absolute + visibility:hidden) so plotly has
  // time to mount and paint before we reveal them — preventing the "No data → data"
  // flash. Keeping the items off-flow means the wrapper height stays at the 220px
  // spinner footprint, so the spinner doesn't get re-centered into the page bottom.
  return (
    <div css={styles.wrapper}>
      {isLoading && (
        <div
          css={styles.spinningContainer}
          className={classNames('dlLoadingSpinnerContainer', 'dlFlexBoxContainerInContainer', {
            dlLoadingSpinnerParent: isLoading,
          })}
        >
          <LoadingSpinner isVisible={isLoading} />
        </div>
      )}
      {!isDataLoading && (
        <div
          css={styles.contentWrapper}
          style={
            chartsReady
              ? undefined
              : {position: 'absolute', top: 0, left: 0, right: 0, visibility: 'hidden'}
          }
          aria-hidden={!chartsReady}
        >
          {sortedChartData.map((singleChartData) => (
            <DiagnosisLaboratoryResultsItem
              key={singleChartData.eventType}
              data={data!}
              singleChartData={singleChartData}
              supportedType={labMethodTypeMap[singleChartData.eventType]}
              phRecommendations={pharmaRecommendations}
              chartEvents={chartEvents}
            />
          ))}
        </div>
      )}
    </div>
  );
};

DiagnosisLaboratoryResults.displayName = 'DiagnosisLaboratoryResults';

const getStyles: GetStylesFunction = (options) => ({
  contentWrapper: css`
    display: flex;
    flex-direction: column;
    gap: ${options?.theme?.spacing(2)};
  `,
  spinningContainer: css`
    width: 100%;
    height: 100%;
  `,
  wrapper: css`
    position: relative;
    display: flex;
    flex-direction: column;
    min-height: ${options?.params?.isLoading ? '220px' : 'unset'};
  `,
  filteringContainerWrapper: css``,
});
